import { moneyString, type StatementImportLine } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';

/**
 * قراءة كشف البنك فى المتصفح: CSV أو XLSX كما يصدّره البنك → صفوف → ربط الأعمدة →
 * سطور موحّدة تُرسل للخادم. الملف الخام لا يغادر المتصفح. XLSX يُقرأ بلا مكتبة جداول
 * (`./xlsx`) ثم يدخل نفس المسار.
 */

const ARABIC_INDIC = /[٠-٩]/g;
const EASTERN_ARABIC = /[۰-۹]/g;

export function normalizeDigits(input: string): string {
  return input
    .replace(ARABIC_INDIC, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(EASTERN_ARABIC, (digit) => String(digit.charCodeAt(0) - 0x06f0));
}

/** الفاصل الأكثر ظهورًا خارج علامات التنصيص فى أول سطر: فاصلة أو فاصلة منقوطة أو Tab. */
function detectDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  let best = ',';
  let bestCount = -1;
  for (const candidate of [',', ';', '\t']) {
    let count = 0;
    let quoted = false;
    for (const char of firstLine) {
      if (char === '"') quoted = !quoted;
      else if (!quoted && char === candidate) count += 1;
    }
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  }
  return best;
}

/** CSV بعلامات تنصيص مضاعفة وأسطر داخل الخلايا، ويُسقط BOM والصفوف الفارغة. */
export function parseCsv(input: string): string[][] {
  const text = input.replace(/^\uFEFF/, '');
  const delimiter = detectDelimiter(text);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (quoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows
    .map((cells) => cells.map((value) => value.trim()))
    .filter((cells) => cells.some((value) => value !== ''));
}

function isRealDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * تاريخ الكشف إلى `YYYY-MM-DD`. البنوك المصرية تكتب اليوم أولاً (31/10/2026)، والسنة
 * فى أول التاريخ تعنى الصيغة الدولية. السنة بخانتين تعنى ٢٠xx. تاريخ مستحيل `null`.
 */
export function parseStatementDate(input: string): string | null {
  const text = normalizeDigits(input).trim().split(/[\sT]/)[0] ?? '';
  let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(text);
  let year: number;
  let month: number;
  let day: number;
  if (match) {
    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
  } else {
    match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(text);
    if (!match) return null;
    day = Number(match[1]);
    month = Number(match[2]);
    year = Number(match[3]);
    if (year < 100) year += 2000;
  }
  if (!isRealDate(year, month, day)) return null;
  return year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
}

/**
 * مبلغ من خلية كشف بإشارته: أرقام هندية، فاصلة عشرية عربية، فواصل آلاف، رمز العملة،
 * والسالب بـ «-» قبله أو بعده أو بين قوسين، و DR/CR فى آخره. الخلية الفارغة `null`
 * (لا صفر) — فى أسلوب عمودى المدين والدائن يكون أحدهما فارغًا دائمًا.
 */
export function parseSignedAmount(input: string): string | null {
  let text = normalizeDigits(input)
    .trim()
    .replace(/٫/g, '.')
    .replace(/[,٬\s\u00a0\u200f\u200e]/g, '')
    .replace(/(EGP|LE|L\.E\.?|ج\.?م\.?|جنيه(ا)?)/gi, '');
  if (text === '' || text === '-') return null;
  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1);
  }
  const suffix = /(DR|CR)$/i.exec(text);
  if (suffix) {
    negative = negative || suffix[1]!.toUpperCase() === 'DR';
    text = text.slice(0, -2);
  }
  if (text.endsWith('-')) {
    negative = !negative;
    text = text.slice(0, -1);
  }
  if (text.startsWith('-')) {
    negative = !negative;
    text = text.slice(1);
  } else if (text.startsWith('+')) {
    text = text.slice(1);
  }
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  // أكثر من أربع خانات عشرية يُرفض صراحة بدل تقريب صامت لمبلغ بنكى
  if (!moneyString.safeParse(text).success) return null;
  const value = Money.of(text);
  return (negative && !value.isZero() ? value.negate() : value).toString();
}

export type AmountMode = 'signed' | 'split';

export interface ColumnMapping {
  /** الصف الأول عناوين أعمدة لا بيانات. */
  hasHeader: boolean;
  date: number;
  description: number | null;
  reference: number | null;
  mode: AmountMode;
  /** عمود المبلغ بإشارته (الإيداع موجب). */
  amount: number | null;
  /** عمود السحب — «مدين» فى لغة كشف البنك. */
  withdrawal: number | null;
  /** عمود الإيداع — «دائن» فى لغة كشف البنك. */
  deposit: number | null;
  balance: number | null;
}

export interface RowIssue {
  /** رقم الصف فى الملف بدءًا من ١. */
  row: number;
  message: string;
}

export interface BuiltStatement {
  lines: StatementImportLine[];
  issues: RowIssue[];
  /** صفوف بلا حركة تُركت عمدًا (رصيد افتتاحى، إجماليات، تذييل). */
  skipped: number;
}

const cellOf = (cells: readonly string[], index: number | null): string =>
  index === null ? '' : (cells[index] ?? '');

/**
 * يطبّق ربط الأعمدة على الصفوف. صف بلا تاريخ صالح ولا مبلغ يُترك (تذييل أو إجمالى)،
 * وصف بتاريخ ومبلغ فاسد يُبلَّغ برقمه — لا يُستورد كشف ناقص بصمت.
 */
export function buildStatement(rows: readonly string[][], mapping: ColumnMapping): BuiltStatement {
  const lines: StatementImportLine[] = [];
  const issues: RowIssue[] = [];
  let skipped = 0;
  rows.forEach((cells, index) => {
    if (mapping.hasHeader && index === 0) return;
    const rowNumber = index + 1;
    const date = parseStatementDate(cellOf(cells, mapping.date));

    let amount: string | null = null;
    let amountInvalid = false;
    if (mapping.mode === 'signed') {
      const raw = cellOf(cells, mapping.amount);
      amount = parseSignedAmount(raw);
      amountInvalid = raw !== '' && amount === null;
    } else {
      const rawOut = cellOf(cells, mapping.withdrawal);
      const rawIn = cellOf(cells, mapping.deposit);
      const out = parseSignedAmount(rawOut);
      const into = parseSignedAmount(rawIn);
      amountInvalid = (rawOut !== '' && out === null) || (rawIn !== '' && into === null);
      if (out !== null || into !== null) {
        amount = Money.of(into ?? '0')
          .abs()
          .sub(Money.of(out ?? '0').abs())
          .toString();
      }
    }

    const rawDate = cellOf(cells, mapping.date);
    // صف لا يحاول تاريخًا (إجمالى، تذييل) أو بلا مبلغ يُترك؛ تاريخ مكتوب فاسد يُبلَّغ
    if (
      date === null &&
      (amount === null || amountInvalid || !/\d/.test(normalizeDigits(rawDate)))
    ) {
      skipped += 1;
      return;
    }
    if (date === null) {
      issues.push({ row: rowNumber, message: 'تاريخ غير مفهوم: ' + rawDate });
      return;
    }
    if (amountInvalid) {
      issues.push({ row: rowNumber, message: 'مبلغ غير مفهوم' });
      return;
    }
    if (amount === null || Money.of(amount).isZero()) {
      skipped += 1;
      return;
    }

    const rawBalance = cellOf(cells, mapping.balance);
    const balance = parseSignedAmount(rawBalance);
    if (rawBalance !== '' && balance === null) {
      issues.push({ row: rowNumber, message: 'رصيد غير مفهوم' });
      return;
    }
    const reference = cellOf(cells, mapping.reference).slice(0, 200);
    lines.push({
      date,
      description: cellOf(cells, mapping.description).slice(0, 500),
      ...(reference ? { reference } : {}),
      amount,
      ...(balance !== null ? { balance } : {}),
    });
  });
  return { lines, issues, skipped };
}

const HINTS: Record<
  'date' | 'description' | 'reference' | 'amount' | 'withdrawal' | 'deposit' | 'balance',
  RegExp
> = {
  date: /تاريخ|date|value/i,
  description: /بيان|وصف|تفاصيل|description|details|narrative|particulars/i,
  reference: /مرجع|رقم|شيك|reference|ref|cheque|check/i,
  withdrawal: /مدين|سحب|مسحوب|debit|withdraw/i,
  deposit: /دائن|إيداع|ايداع|مودع|credit|deposit/i,
  amount: /مبلغ|قيمة|amount/i,
  balance: /رصيد|balance/i,
};

/** تخمين أولى للربط من عناوين الأعمدة — المستخدم يراجعه قبل الاستيراد. */
export function guessMapping(header: readonly string[]): ColumnMapping {
  const used = new Set<number>();
  const find = (hint: RegExp): number | null => {
    const index = header.findIndex((title, position) => !used.has(position) && hint.test(title));
    if (index < 0) return null;
    used.add(index);
    return index;
  };
  // الرصيد قبل المبلغ: «رصيد» قد يحوى كلمة قيمة، والعكس نادر
  const date = find(HINTS.date);
  const balance = find(HINTS.balance);
  const withdrawal = find(HINTS.withdrawal);
  const deposit = find(HINTS.deposit);
  const amount = withdrawal === null || deposit === null ? find(HINTS.amount) : null;
  const description = find(HINTS.description);
  const reference = find(HINTS.reference);
  const hasHeader = [date, balance, withdrawal, deposit, amount, description].some(
    (value) => value !== null,
  );
  return {
    hasHeader,
    date: date ?? 0,
    description,
    reference,
    mode: withdrawal !== null && deposit !== null ? 'split' : 'signed',
    amount,
    withdrawal,
    deposit,
    balance,
  };
}
